import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CardList from 'components/CardList/CardList';
import Label from 'components/_utility/Label';
import PackCard from 'components/_cards/PackCard/PackCard';
import { getPacks } from 'src/redux/configurator/selectors';
import { getPack, getPreviewedPack, getPackSelectedAccessories, getPackSelectedTotal } from 'src/redux/bike/selectors';
import AccessoryCard from 'components/_cards/AccessoryCard/AccessoryCard';
import { setSelectedDisclaimer, openModal, setScrollYPosition } from 'src/redux/configurator/actions';
import Cross from 'src/assets/icons/close.svg';
import usePrice from 'src/hooks/usePrice';
import {
  removePack,
  setPreviewedPack,
  addPackAccessory,
  removePackAccessory,
  clearPreviewedPack,
} from 'src/redux/bike/actions';
import { isWindows } from 'helpers/utilities';
import InfoCircle from 'src/assets/icons/info-circle.svg';
import useDataLayer from 'src/hooks/useDataLayer';
import ChevronLeftIcon from 'src/assets/icons/chevron-left.svg';
import EyeSlashIcon from 'src/assets/icons/eye-slash.svg';
import Modal from 'components/_utility/Modal/Modal';
import {
  Wrap,
  Title,
  TitleWrap,
  Description,
  Subtitle,
  Summary,
  Total,
  RemoveButton,
  TotalLabel,
  TotalAmount,
  InfoCircleWrap,
} from './ChoosePack.styled';
import { VisibilityNotice, VisibilityModal } from '../ChooseAccessories/ChooseAccessories.styled';

const ChoosePack = () => {
  const [visibilityModalOpen, setVisibilityModalOpen] = useState(false);
  const dispatch = useDispatch();
  const formatPrice = usePrice();
  const packs = useSelector(getPacks);
  const selectedPack = useSelector(getPack);
  const previewedPack = useSelector(getPreviewedPack);
  const pushToDataLayer = useDataLayer();
  const packSelectedAccessories = useSelector(getPackSelectedAccessories);
  const packSelectedAccessoryIds = packSelectedAccessories.map(accessory => accessory.id);
  const packTotal = useSelector(getPackSelectedTotal);

  const visiblePack = selectedPack || previewedPack;

  const getPackById = packId => packs.find(pack => pack.id === packId);

  const handleSelectPack = packId => {
    const newSelectedPack = getPackById(packId);

    pushToDataLayer('configuratorPackInfo', {
      configuratorPackName: newSelectedPack.name,
      configuratorPackID: newSelectedPack.id,
      configuratorPackPrice: newSelectedPack.price,
    });

    dispatch(setScrollYPosition(0));
    dispatch(setPreviewedPack(getPackById(packId)));
  };

  const handleRemovePack = () => [
    pushToDataLayer('configuratorPackRemoved', {
      configuratorPackName: selectedPack.name,
      configuratorPackID: selectedPack.id,
      configuratorPackPrice: selectedPack.price,
    }),
    dispatch(removePack()),
    dispatch(clearPreviewedPack()),
  ];

  const handleSelectCard = accessoryId => {
    if (visiblePack.isLocked) {
      return;
    }

    dispatch(
      packSelectedAccessoryIds.includes(accessoryId)
        ? removePackAccessory(accessoryId)
        : addPackAccessory(visiblePack.parts.find(accessory => accessory.id === accessoryId))
    );
  };

  return (
    <Wrap accessoryView={Boolean(visiblePack)}>
      {visiblePack ? (
        <>
          {previewedPack ? (
            <TitleWrap isWindows={isWindows} onClick={() => dispatch(clearPreviewedPack())}>
              <ChevronLeftIcon />
              <Title hasSubtitle previewedPack={previewedPack}>
                {visiblePack.name}
              </Title>
            </TitleWrap>
          ) : (
            <Title hasSubtitle>{visiblePack.name}</Title>
          )}
          <Subtitle>
            {visiblePack.parts.length} <Label name="packItemsLabel" />
          </Subtitle>
          <Description dangerouslySetInnerHTML={{ __html: visiblePack.description }} accessoryView />
          <Summary>
            <Total>
              <TotalLabel>
                <Label name="packTotalLabel" />
              </TotalLabel>
              <TotalAmount>{formatPrice(packTotal)}</TotalAmount>
              <InfoCircleWrap
                onClick={() => {
                  dispatch(setSelectedDisclaimer('packTotal'));
                  dispatch(openModal('disclaimer'));
                }}
              >
                <InfoCircle />
              </InfoCircleWrap>
            </Total>
            {selectedPack ? (
              <RemoveButton onClick={handleRemovePack}>
                <Cross /> <Label name="removeLabel"></Label>
              </RemoveButton>
            ) : null}
          </Summary>
          <VisibilityNotice>
            <EyeSlashIcon /> <Label name="accessoriesVisibilityDisclaimer" />
          </VisibilityNotice>
          <CardList
            cardComponent={AccessoryCard}
            cards={visiblePack.parts}
            onSelectCard={handleSelectCard}
            selectedCardIds={packSelectedAccessoryIds}
            onSelectEye={event => {
              event.stopPropagation();

              setVisibilityModalOpen(true);
            }}
            horizontalScroll
          />
        </>
      ) : (
        <>
          <Title>
            <Label name="choosePackTitle" />
          </Title>
          <Description>
            <Label name="choosePackDescription" />
          </Description>
          <CardList cardComponent={PackCard} cards={packs} onSelectCard={handleSelectPack} horizontalScroll packs />
        </>
      )}
      {visibilityModalOpen ? (
        <Modal onClose={() => setVisibilityModalOpen(false)} isMessage>
          <VisibilityModal>
            <VisibilityNotice isInModal>
              <EyeSlashIcon /> <Label name="accessoriesVisibilityDisclaimer" />
            </VisibilityNotice>
          </VisibilityModal>
        </Modal>
      ) : null}
    </Wrap>
  );
};

export default ChoosePack;
